import type {CopilotPlan} from '@github-ui/copilot-chat-types'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useEffect, useRef} from 'react'

interface IndividualExhaustedAnalyticsContext {
  category: 'general_ipm_ubb_user_exhausted_alerts_individual'
  scope: 'personal'
  surface: 'copilot_chat_quota_banner'
}

interface UseQuotaBannerShownTelemetryOptions {
  target?: string
  enabled?: boolean
  dedupeKey?: string
  mode: 'assistive' | 'immersive'
  plan: CopilotPlan | null | undefined
  tokenBasedBillingEnabled: boolean | undefined
  variant?: string | null
  quotaUsage?: number | null
  analyticsContext?: IndividualExhaustedAnalyticsContext
}

export function useQuotaBannerShownTelemetry({
  target = 'QUOTA_BANNER_EXCEEDED',
  enabled = true,
  dedupeKey,
  mode,
  plan,
  tokenBasedBillingEnabled,
  variant,
  quotaUsage,
  analyticsContext,
}: UseQuotaBannerShownTelemetryOptions) {
  const sentKeysRef = useRef<Set<string>>(new Set())

  useEffect(() => {
    const sentKeys = sentKeysRef.current
    return () => {
      sentKeys.clear()
    }
  }, [])

  useEffect(() => {
    if (!enabled) return

    const eventKey = dedupeKey ?? '__default__'
    if (sentKeysRef.current.has(eventKey)) return

    sentKeysRef.current.add(eventKey)

    sendEvent('dotcom_chat.activate', {
      target,
      action: 'shown',
      mode,
      tokenBasedBillingEnabled,
      plan,
      variant,
      quotaUsage,
      ...(analyticsContext ?? {}),
    })
  }, [analyticsContext, dedupeKey, enabled, mode, plan, target, tokenBasedBillingEnabled, variant, quotaUsage])
}
